<template>
    <slot name="stone-type"></slot>

    <FilterAccordion
        v-qa="'filter-price'"
        :count="
            values?.priceInCurrency?.min?.amount &&
            values.priceInCurrency?.max?.amount
                ? 1
                : 0
        "
        :title="t('diamond_search.filter.price.input')"
        hide-count
    >
        <slot name="price"></slot>
    </FilterAccordion>

    <FilterAccordion
        v-qa="'filter-carat-size'"
        :count="weight?.min && weight?.max ? 1 : 0"
        :title="t('diamond_search.filter.carat_size.input')"
        hide-count
    >
        <FilterRangeSegmented
            v-model="weight"
            :segments-config="
                getCaratSegmentsConfig(bounds.weight.min, bounds.weight.max)
            "
            :min-label="'Minimum carat weight'"
            :max-label="'Maximum carat weight'"
            :bounds="bounds.weight"
            :value-formatter="formatNumericProperty"
        />
    </FilterAccordion>

    <FilterAccordion
        v-qa="'filter-gemstone-type'"
        :count="typesModel.length"
        :title="t('diamond_search.filter.gemstone_type.input')"
    >
        <template #default="{ disabled: disabledAccordion }">
            <GemstoneTypeFilter
                v-model="typesModel"
                v-qa="'filter-gemstone-type'"
                :bounds="bounds.gemstoneType || []"
                :disabled="disabledAccordion"
                is-multi
            />
        </template>
    </FilterAccordion>

    <div
        v-if="isAdmin"
        v-qa="'consultant-filters'"
        class="consultant-filters"
    >
        <div class="consultant-filters-title">Consultant filters</div>

        <FilterDeliveryDays
            v-model="deliveryDays"
            :bounds="bounds.deliveryDays"
        />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { GemstoneSearchFilters } from '@/domain/stone/gemstone'
import type {
    GemsApi_GemstoneTypeFilter,
    GemsApi_GemstoneTypeFilterInput,
    GemsApi_StoneShape,
    GemsBoundsFragment,
} from '@/graphql/api'

import { useFilterBindings } from '@/components/ui/filter/useFilterBindings'
import { useIsAdmin } from '@/components/app/isAdmin.ts'
import { formatNumericProperty } from '@/domain/stone/labels'
import { getCaratSegmentsConfig } from '../input-range-config'
import { isGemstoneTypeFilterInputSameAsFilter } from '@/domain/stone/search/sku/gemstone'

import FilterAccordion from '@/components/ui/filter/FilterAccordion.vue'
import FilterDeliveryDays from '@/components/stone/search/sku/filter/FilterDeliveryDays.vue'
import GemstoneTypeFilter from '@/components/stone/search/sku/filter/GemstoneTypeFilter.vue'
import FilterRangeSegmented from './filter/FilterRangeSegmented.vue'

const { bounds, values } = defineProps<{
    bounds: NonNullable<GemsBoundsFragment>
    values: GemstoneSearchFilters
    shape: GemsApi_StoneShape | undefined | null
}>()

const emit = defineEmits<{
    'update:values': [v: GemstoneSearchFilters]
}>()

const { t } = useI18n()

const isAdmin = useIsAdmin()

const { weight, deliveryDays } = useFilterBindings(
    computed(() => values),
    {},
    emit
)

const typesModel = computed({
    get: (): GemsApi_GemstoneTypeFilter[] => {
        return (
            bounds.gemstoneType?.filter((t) => {
                return values.gemstoneTypes?.some((f) =>
                    isGemstoneTypeFilterInputSameAsFilter(f, t)
                )
            }) || []
        )
    },
    set: (value: GemsApi_GemstoneTypeFilter[]) => {
        emit('update:values', {
            ...values,
            gemstoneTypes: value.map(
                (
                    t: GemsApi_GemstoneTypeFilter
                ): GemsApi_GemstoneTypeFilterInput => {
                    return {
                        colour: t.colour,
                        crystal: t.crystal,
                    }
                }
            ),
        })
    },
})
</script>

<style lang="scss" scoped>
.consultant-filters {
    margin-top: 20px;
    margin-bottom: 20px;

    .consultant-filters-title {
        margin: 0 0 20px;
        font-family: $effra-font-stack;
        font-size: 14px;
        text-transform: uppercase;
        font-weight: 400;
        line-height: 15px;
        letter-spacing: 0.05em;
    }
}
</style>
